import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../hooks/usePreferences';
import '../components/AuthModal.css';
import './PreferenceOnboarding.css';

const NEIGHBORHOODS_BY_CITY = {
  houston: ['Montrose', 'The Heights', 'River Oaks', 'Midtown', 'Bellaire', 'West University', 'Memorial', 'Museum District'],
  austin: ['Zilker', 'Hyde Park', 'Travis Heights', 'East Austin', 'Mueller', 'Barton Hills'],
  dallas: ['Uptown', 'Bishop Arts', 'Lakewood', 'Preston Hollow', 'Deep Ellum'],
  'san antonio': ['Alamo Heights', 'Southtown', 'Stone Oak', 'The Pearl'],
};

const TIMELINES = [
  ['0-3mo', '0–3 months'],
  ['3-6mo', '3–6 months'],
  ['6-12mo', '6–12 months'],
  ['browsing', 'just browsing'],
];

const FEATURES = ['garage', 'pool', 'backyard', 'home office', 'updated kitchen', 'open floor plan', 'good school district', 'walkable'];

const LIFE_STAGES = [
  ['first-time', 'first-time buyer'],
  ['growing-family', 'growing family'],
  ['downsizing', 'downsizing'],
  ['relocating', 'relocating for work'],
  ['investor', 'investor'],
];

const STEPS = ['rent or buy', 'timeline', 'location', 'home', 'features', 'about you'];

export default function PreferenceOnboarding() {
  const { user, profile } = useAuth();
  const { preferences, loading, savePreferences } = usePreferences();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    intent: '',
    timeline: '',
    city: '',
    state: '',
    neighborhood: '',
    min_bedrooms: '',
    budget_min: '',
    budget_max: '',
    special_features: [],
    life_stage: '',
    school_district: '',
    employer: '',
  });

  const isBuyer = !profile?.role || profile.role === 'buyer' || profile.role === 'both';
  const shouldShow = !!user && isBuyer && !loading && !preferences?.completed_at && !dismissed;

  if (!shouldShow) return null;

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function toggleFeature(feature) {
    setForm(f => ({
      ...f,
      special_features: f.special_features.includes(feature)
        ? f.special_features.filter(x => x !== feature)
        : [...f.special_features, feature],
    }));
  }

  async function handleFinish() {
    setSaving(true);
    const { error } = await savePreferences({
      intent: form.intent || null,
      timeline: form.timeline || null,
      city: form.city.trim() || null,
      state: form.state.trim().toUpperCase() || null,
      neighborhood: form.neighborhood || null,
      min_bedrooms: form.min_bedrooms ? Number(form.min_bedrooms) : null,
      budget_min: form.budget_min ? Number(form.budget_min) : null,
      budget_max: form.budget_max ? Number(form.budget_max) : null,
      special_features: form.special_features,
      life_stage: form.life_stage || null,
      school_district: form.school_district.trim() || null,
      employer: form.employer.trim() || null,
      completed_at: new Date().toISOString(),
    });
    setSaving(false);

    if (!error) {
      navigate(form.intent === 'rent' ? '/rentals' : '/shortlist');
    }
  }

  const neighborhoodOptions = NEIGHBORHOODS_BY_CITY[form.city.trim().toLowerCase()] || [];

  return (
    <div className="modal-overlay">
      <div className="modal-box onboarding-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setDismissed(true)}>✕</button>
        <div className="modal-logo">tell us what you're looking for</div>
        <p className="onboarding-sub">A few quick questions so we can personalize your shortlist and bid alerts. Takes under a minute.</p>

        <div className="onboarding-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`onboarding-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="input-group">
            <label>are you looking to rent or buy?</label>
            <div className="role-selector">
              {[['buy', 'Buy', "I want to bid on a home to purchase"],
                ['rent', 'Rent', "I want to find a rental"]
              ].map(([val, label, sub]) => (
                <div
                  key={val}
                  className={`role-opt ${form.intent === val ? 'selected' : ''}`}
                  onClick={() => update('intent', val)}
                >
                  <div className="role-label">I want to {label.toLowerCase()}</div>
                  <div className="role-sub">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="input-group">
            <label>{form.intent === 'rent' ? 'when are you hoping to move in?' : 'when are you hoping to buy?'}</label>
            <div className="chip-row">
              {TIMELINES.map(([val, label]) => (
                <div key={val} className={`chip ${form.timeline === val ? 'selected' : ''}`} onClick={() => update('timeline', val)}>{label}</div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="input-group">
              <label>city</label>
              <input value={form.city} onChange={e => update('city', e.target.value)} placeholder="Houston" />
            </div>
            <div className="input-group">
              <label>state</label>
              <input value={form.state} onChange={e => update('state', e.target.value)} placeholder="TX" maxLength={2} />
            </div>
            <div className="input-group">
              <label>neighborhood (optional)</label>
              {neighborhoodOptions.length > 0 ? (
                <select value={form.neighborhood} onChange={e => update('neighborhood', e.target.value)}>
                  <option value="">any neighborhood</option>
                  {neighborhoodOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <input value={form.neighborhood} onChange={e => update('neighborhood', e.target.value)} placeholder="e.g. Montrose" />
              )}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="input-group">
              <label>minimum bedrooms</label>
              <select value={form.min_bedrooms} onChange={e => update('min_bedrooms', e.target.value)}>
                <option value="">any</option>
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}+</option>)}
              </select>
            </div>
            <div className="input-group">
              <label>{form.intent === 'rent' ? 'monthly budget' : 'purchase budget'}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="number" min="0" value={form.budget_min} onChange={e => update('budget_min', e.target.value)} placeholder={form.intent === 'rent' ? 'min $/mo' : 'min $'} />
                <input type="number" min="0" value={form.budget_max} onChange={e => update('budget_max', e.target.value)} placeholder={form.intent === 'rent' ? 'max $/mo' : 'max $'} />
              </div>
            </div>
          </>
        )}

        {step === 4 && (
          <div className="input-group">
            <label>special features you'd love</label>
            <div className="chip-row">
              {FEATURES.map(f => (
                <div key={f} className={`chip ${form.special_features.includes(f) ? 'selected' : ''}`} onClick={() => toggleFeature(f)}>{f}</div>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <>
            <div className="input-group">
              <label>life stage</label>
              <div className="chip-row">
                {LIFE_STAGES.map(([val, label]) => (
                  <div key={val} className={`chip ${form.life_stage === val ? 'selected' : ''}`} onClick={() => update('life_stage', val)}>{label}</div>
                ))}
              </div>
            </div>
            <div className="input-group">
              <label>school district (optional)</label>
              <input value={form.school_district} onChange={e => update('school_district', e.target.value)} placeholder="e.g. Houston ISD" />
            </div>
            <div className="input-group">
              <label>employer (optional)</label>
              <input value={form.employer} onChange={e => update('employer', e.target.value)} placeholder="helps us find homes near your commute" />
            </div>
          </>
        )}

        <div className="onboarding-actions">
          {step > 0 && <button className="btn btn-sm" onClick={() => setStep(s => s - 1)}>back</button>}
          <div style={{ flex: 1 }} />
          <button className="btn btn-sm" onClick={() => setDismissed(true)}>skip for now</button>
          {step < STEPS.length - 1 ? (
            <button
              className="btn btn-primary btn-sm"
              disabled={step === 0 && !form.intent}
              onClick={() => setStep(s => s + 1)}
            >
              next
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleFinish}>
              {saving ? 'saving...' : 'finish'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
